"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/utils/cn";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { LANDING, href } from "./landingDict";
import { localeNavHref } from "@/config/localeRoutes";
import serviceADisposition from "@/assets/images/landing/services/A disposition.png";
import serviceAeroportGares from "@/assets/images/landing/services/Aeroport & Gares.png";
import serviceCabriolets from "@/assets/images/landing/services/Cabriolets.png";
import serviceEntreprises from "@/assets/images/landing/services/Entreprises.png";
import serviceEvenements from "@/assets/images/landing/services/Evenements.png";
import serviceLivraisonCollecte from "@/assets/images/landing/services/LivraisonCollecte.png";
import servicePrestige from "@/assets/images/landing/services/Prestige.png";
import serviceSuvBerlines from "@/assets/images/landing/services/SUV & Berlines.png";

type ServicesSectionProps = {
  className?: string;
  locale?: Locale;
};

// Visuels identiques FR/EN ; les libellés viennent du dictionnaire.
const CHAUFFEUR_IMAGES = [
  serviceAeroportGares,
  serviceADisposition,
  serviceEvenements,
  serviceEntreprises,
] as const;

const LOCATION_IMAGES = [
  servicePrestige,
  serviceSuvBerlines,
  serviceCabriolets,
  serviceLivraisonCollecte,
] as const;

// MESURE 2026-10-01 : le contenu de l'accueil, seule page du site qui a de
// l'autorité (position 11,7 contre 30 à 50 pour les pages ville), ne faisait
// aucun lien vers les pages qui vendent. Chaque tuile mène à sa page, dans le
// même ordre que driverItems / rentalItems. Chemins FR, localisés au rendu.
const CHAUFFEUR_HREFS = [
  "/chauffeur/transferts/cannes-aeroport-nice",
  "/location/avec-chauffeur",
  "/chauffeur/evenements",
  "/chauffeur/entreprise",
] as const;

const LOCATION_HREFS = [
  "/location/prestige",
  "/location/suv",
  "/location/cabriolet",
  "/location/location-voiture-cannes",
] as const;

/** Pages chauffeur par ville, dans l'ordre de `driverCities`. */
const DRIVER_CITY_HREFS = [
  "/chauffeur/cannes",
  "/chauffeur/antibes",
  "/chauffeur/nice",
  "/chauffeur/monaco",
  "/chauffeur/saint-tropez",
] as const;

const Card = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <motion.div
    initial={false}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10%" }}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={cn(
      "group relative rounded-[2rem] p-[2px]",
      "bg-black/10",
      "shadow-[0_0_0_1px_rgba(0,0,0,0.08)]",
      className,
    )}
  >
    <div
      className={cn(
        "relative h-full w-full rounded-[calc(2rem-2px)] bg-white p-6 ph:p-7 md:p-10 lg:p-12",
        "min-h-[22rem] border border-black/10 backdrop-blur-md ph:min-h-[26rem] md:min-h-[28rem]",
      )}
    >
      {/* Glow */}
      <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-primary/20 opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-100 ph:-right-16 ph:-top-16 ph:h-60 ph:w-60" />
      {children}
    </div>
  </motion.div>
);

export const ServicesSection = ({
  className,
  locale = "fr",
}: ServicesSectionProps) => {
  const t = tr(LANDING, locale).services;
  const chauffeurItems = t.driverItems.map((label, index) => ({
    label,
    image: CHAUFFEUR_IMAGES[index],
    href: localeNavHref(CHAUFFEUR_HREFS[index], locale),
  }));
  const locationItems = t.rentalItems.map((label, index) => ({
    label,
    image: LOCATION_IMAGES[index],
    href: localeNavHref(LOCATION_HREFS[index], locale),
  }));
  const cityLinks = t.driverCities.map((label, index) => ({
    label,
    href: localeNavHref(DRIVER_CITY_HREFS[index], locale),
  }));
  return (
    <section
      className={
        "relative w-full overflow-x-hidden bg-white py-20 text-black ph:py-24 md:py-28 " +
        (className ?? "")
      }
    >
      <div className="mx-auto max-w-[90rem] px-4 ph:px-6 md:px-8">
        <div className="mb-12 text-center ph:mb-16">
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            className="mb-4 text-sm uppercase tracking-[0.35em] text-black/60"
          >
            {t.eyebrow}
          </motion.p>
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, margin: "-10%" }}
            className="font-space-age text-2xl tracking-[0.2em] text-black ph:text-3xl ph:tracking-[0.25em] md:text-4xl"
          >
            {t.heading}
          </motion.h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-black/70 ph:text-lg">
            {t.intro}
          </p>
        </div>

        <div className="grid gap-10 ph:gap-12 md:grid-cols-2 xl:gap-20">
          {/* Chauffeur privé */}
          <Card>
            <div className="mb-5 flex items-center justify-between ph:mb-6">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 ph:h-12 ph:w-12">
                  <Icon
                    icon="mdi:steering"
                    className="h-5 w-5 text-primary ph:h-6 ph:w-6"
                  />
                </span>
                <h3 className="text-xl font-semibold ph:text-2xl">
                  {t.driverTitle}
                </h3>
              </div>
              <span className="rounded-full border border-black/10 px-3 py-1 text-[11px] text-black/70 ph:px-4 ph:py-1.5 ph:text-xs">
                {t.driverBadge}
              </span>
            </div>
            <p className="mb-5 leading-relaxed text-black/80 ph:mb-6">
              {t.driverLead}
            </p>
            <p className="mb-5 text-sm leading-relaxed text-black/65 ph:mb-6">
              {t.driverSub}
            </p>

            <div className="mb-7 grid grid-cols-1 gap-2 text-sm text-black/80 ph:mb-8 ph:grid-cols-2 ph:gap-3">
              {chauffeurItems.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="overflow-hidden rounded-lg border border-black/10 bg-black/[0.02] transition-colors hover:border-primary"
                >
                  <div className="relative aspect-[21/9] w-full">
                    <Image
                      src={item.image}
                      alt={item.label}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 25vw"
                    />
                  </div>
                  <div className="inline-flex min-h-11 w-full items-center gap-2 px-3 py-2 ph:px-4">
                    <Icon
                      icon="mdi:check"
                      className="h-4 w-4 text-primary ph:h-5 ph:w-5"
                    />
                    <span>{item.label}</span>
                  </div>
                </Link>
              ))}
            </div>

            <nav
              aria-label={t.driverCitiesLabel}
              className="mb-7 flex flex-wrap items-center gap-2 text-sm ph:mb-8"
            >
              <span className="text-black/60">{t.driverCitiesLabel}</span>
              {cityLinks.map((city) => (
                <Link
                  key={city.href}
                  href={city.href}
                  className="rounded-full border border-black/15 px-3 py-1.5 text-black/85 transition-colors hover:border-primary hover:bg-primary/5"
                >
                  {city.label}
                </Link>
              ))}
            </nav>

            <div className="flex flex-wrap items-center gap-3 ph:gap-4">
              <Link
                href={href(locale, "/reservation")}
                className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 font-semibold text-black shadow-[0_8px_20px_rgba(196,153,45,0.22)] transition-all hover:-translate-y-0.5 hover:bg-gold-600 hover:shadow-[0_12px_24px_rgba(196,153,45,0.28)] ph:px-6 ph:py-3 sm:w-auto"
              >
                {t.driverCtaPrimary}
                <Icon
                  icon="mdi:arrow-right"
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                href={href(locale, "/chauffeur/services")}
                className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-black/15 bg-white px-5 py-2.5 text-black/90 transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-primary/5 ph:px-6 ph:py-3 sm:w-auto"
              >
                {t.driverCtaSecondary}
                <Icon
                  icon="mdi:information-outline"
                  className="transition-transform duration-200 group-hover:scale-105"
                />
              </Link>
            </div>
          </Card>

          {/* Location de véhicules */}
          <Card>
            <div className="mb-5 flex items-center justify-between ph:mb-6">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 ph:h-12 ph:w-12">
                  <Icon
                    icon="mdi:car-sports"
                    className="h-5 w-5 text-primary ph:h-6 ph:w-6"
                  />
                </span>
                <h3 className="text-xl font-semibold ph:text-2xl">
                  {t.rentalTitle}
                </h3>
              </div>
              <span className="rounded-full border border-black/10 px-3 py-1 text-[11px] text-black/70 ph:px-4 ph:py-1.5 ph:text-xs">
                {t.rentalBadge}
              </span>
            </div>
            <p className="mb-5 leading-relaxed text-black/80 ph:mb-6">
              {t.rentalLead}
            </p>
            <p className="mb-5 text-sm leading-relaxed text-black/65 ph:mb-6">
              {t.rentalSub}
            </p>

            <div className="mb-7 grid grid-cols-1 gap-2 text-sm text-black/80 ph:mb-8 ph:grid-cols-2 ph:gap-3">
              {locationItems.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="overflow-hidden rounded-lg border border-black/10 bg-black/[0.02] transition-colors hover:border-primary"
                >
                  <div className="relative aspect-[21/9] w-full">
                    <Image
                      src={item.image}
                      alt={item.label}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 25vw"
                    />
                  </div>
                  <div className="inline-flex min-h-11 w-full items-center gap-2 px-3 py-2 ph:px-4">
                    <Icon
                      icon="mdi:check"
                      className="h-4 w-4 text-primary ph:h-5 ph:w-5"
                    />
                    <span>{item.label}</span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 ph:gap-4">
              <Link
                href={href(locale, "/location/vehicules#catalogue-location")}
                className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 font-semibold text-black shadow-[0_8px_20px_rgba(196,153,45,0.22)] transition-all hover:-translate-y-0.5 hover:bg-gold-600 hover:shadow-[0_12px_24px_rgba(196,153,45,0.28)] ph:px-6 ph:py-3 sm:w-auto"
              >
                {t.rentalCtaPrimary}
                <Icon
                  icon="mdi:arrow-right"
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                href={href(locale, "/contact")}
                className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-black/15 bg-white px-5 py-2.5 text-black/90 transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-primary/5 ph:px-6 ph:py-3 sm:w-auto"
              >
                {t.rentalCtaSecondary}
                <Icon
                  icon="mdi:file-document-edit-outline"
                  className="transition-transform duration-200 group-hover:scale-105"
                />
              </Link>
            </div>
          </Card>
        </div>

        {/* Note de crédibilité */}
        <div className="mt-16 text-center text-base text-black/60">
          {t.note}
        </div>
      </div>
    </section>
  );
};
